import type { CSSProperties } from 'react';
import { Viewport } from '@/theme/breakpoints';
import { GradientPreset, getGradientPresetString } from '@/theme/gradients';

export const CONTROLS_ANIMATION = {
  ease: [0.4, 0, 0.2, 1] as const,
  duration: 0.5,
  delay: 2.0,
  arrowDistance: 40,
  buildYourOwnDistance: 20,
} as const;

export const GLASSY_CARD_BACKGROUND_STYLE: CSSProperties = {
  backgroundImage: getGradientPresetString(GradientPreset.GlassyCard),
};

export const EQUIP_PILL_CONTAINER_STYLE: CSSProperties = {
  ...GLASSY_CARD_BACKGROUND_STYLE,
  padding: '8px',
};

/**
 * The Deploy | All Teams pill's DOM hook — the value of its
 * `data-cabal-control` attribute. A purpose-named constant shared
 * producer-to-consumer (settled 2026-08-28): a selector imports this, never a
 * retyped string.
 */
export const DEPLOY_ALL_TEAMS_PILL_CONTROL = 'deploy-all-teams-pill';

/**
 * Labels in the control cluster never wrap. The pill and the ghost row are
 * content-sized, so a two-word label ("All Teams", "Quick Start") folding onto
 * two lines would be a squeezed row masquerading as a fit — the same nowrap
 * Cbutton applies on its own `label` path, applied to the cluster's children
 * path.
 */
export const CONTROL_LABEL_STYLE: CSSProperties = {
  whiteSpace: 'nowrap',
};

export enum HeroCarouselControlsAnimationEvent {
  LeftArrowStart = 'left_arrow_start',
  LeftArrowComplete = 'left_arrow_complete',
  DeployAllTeamsPillStart = 'deploy_all_teams_pill_start',
  DeployAllTeamsPillComplete = 'deploy_all_teams_pill_complete',
  RightArrowStart = 'right_arrow_start',
  RightArrowComplete = 'right_arrow_complete',
  BuildQuickStartRowStart = 'build_quick_start_row_start',
  BuildQuickStartRowComplete = 'build_quick_start_row_complete',
}

export enum HeroCarouselControlsViewport {
  Mobile = 'mobile',
  Desktop = 'desktop',
}

export const TOP_ROW_GAP_BY_VIEWPORT: Record<HeroCarouselControlsViewport, number> = {
  [HeroCarouselControlsViewport.Mobile]: 35,
  [HeroCarouselControlsViewport.Desktop]: 80,
};

export interface ResolveHeroCarouselControlsViewportInput {
  viewport: Viewport | null;
}

export type HeroCarouselControlsAnimationEventHandler = (eventName: string) => void;

export function resolveHeroCarouselControlsViewport({
  viewport,
}: ResolveHeroCarouselControlsViewportInput): HeroCarouselControlsViewport {
  return viewport === Viewport.Mobile
    ? HeroCarouselControlsViewport.Mobile
    : HeroCarouselControlsViewport.Desktop;
}
